import SoftBadge from "@/components/atoms/SoftBadge";
import SignedConventionsBarChart from "@/components/molecules/charts/SignedConventionsBarChart";
import SignedConventionStatsFilters, {
  SignedConventionStatsFilterState,
} from "@/components/molecules/table-filters/SignedConventionStatsFilters";
import { getColorFromRole, getReelRoleName } from "@/helpers/UserHelpers";
import { UserRoleType } from "@/interfaces/IUser";
import { SignedConventionStatsParams } from "@/interfaces/SignedConventionStats";
import { useGetSignedConventionStatsQuery } from "@/services/admin/signedConventionStats";
import { DateTime } from "luxon";
import { FC, useMemo, useState } from "react";
import { Card, Col, Row, Spinner, Table } from "react-bootstrap";

const defaultFilters = (): SignedConventionStatsFilterState => ({
  startDate: DateTime.now().minus({ days: 90 }).toISODate()!,
  endDate: DateTime.now().toISODate()!,
  period: "day",
  groupBy: "user",
  users: [],
  roles: [],
});

const KPI_ROLES: UserRoleType[] = ["AE", "SDR"];

const SignedConventionStatsView: FC = () => {
  const [filters, setFilters] = useState<SignedConventionStatsFilterState>(
    defaultFilters,
  );

  const queryParams = useMemo<SignedConventionStatsParams>(
    () => ({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      period: filters.period,
      groupBy: filters.groupBy,
      userIds: filters.users.length
        ? filters.users.map((u) => u.id)
        : undefined,
      roles: filters.roles.length
        ? filters.roles.map((r) => r.value)
        : undefined,
    }),
    [filters],
  );

  const { data, isFetching } = useGetSignedConventionStatsQuery(queryParams);

  const totalsByRole = data?.totalsByRole ?? {};

  const handleChange = (patch: Partial<SignedConventionStatsFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const handleReset = () => setFilters(defaultFilters());

  const renderRoles = (roles: UserRoleType[]) => {
    if (!roles.length) {
      return <SoftBadge bg="secondary">Inconnu</SoftBadge>;
    }
    return (
      <div className="d-flex flex-wrap gap-1">
        {roles.map((role) => (
          <SoftBadge key={role} bg={getColorFromRole([role])}>
            {getReelRoleName([role])}
          </SoftBadge>
        ))}
      </div>
    );
  };

  return (
    <div className="formalys-page formalys-list-page">
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Conventions signées</h4>
          <p className="text-muted mb-0 fs--1">
            Attribution par AE (commercial) et SDR (télépro) assignés sur
            chaque formation. Une convention est créditée à l'AE et au SDR
            assignés.
          </p>
        </Col>
      </Row>

      <SignedConventionStatsFilters
        filters={filters}
        onChange={handleChange}
        onReset={handleReset}
      />

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="h-100 shadow-none border">
            <Card.Body>
              <div className="text-muted fs--1">Total signées</div>
              <div className="fs-2 fw-bold">
                {isFetching ? <Spinner size="sm" /> : (data?.grandTotal ?? 0)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        {KPI_ROLES.map((role) => (
          <Col md={3} key={role}>
            <Card className="h-100 shadow-none border">
              <Card.Body>
                <div className="text-muted fs--1">
                  {getReelRoleName([role])}
                </div>
                <div className="fs-2 fw-bold">{totalsByRole[role] ?? 0}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mb-3 shadow-none border">
        <Card.Body>
          {isFetching && !data ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : data ? (
            <SignedConventionsBarChart
              buckets={data.buckets}
              series={data.series}
              period={data.period}
            />
          ) : null}
        </Card.Body>
      </Card>

      {filters.groupBy === "role" && (
        <div className="alert alert-info py-2 fs--1 mb-3">
          Chaque convention est créditée à l'AE et au SDR assignés sur la
          formation : une convention avec AE + SDR apparaît dans les deux
          barres. Le total ci-dessus reste le nombre distinct de conventions
          signées.
        </div>
      )}

      <Card className="shadow-none border">
        <Card.Body className="p-0">
          <Table responsive striped hover className="mb-0 fs--1">
            <thead>
              <tr>
                <th>{filters.groupBy === "user" ? "Utilisateur" : "Rôle"}</th>
                <th>Rôle(s)</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {data?.series.length ? (
                data.series.map((s) => (
                  <tr key={s.key}>
                    <td>
                      {filters.groupBy === "role"
                        ? getReelRoleName(s.roles)
                        : s.label}
                    </td>
                    <td>{renderRoles(s.roles)}</td>
                    <td className="text-end fw-semibold">{s.total}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    Aucune donnée
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SignedConventionStatsView;
